// Field configurations for all asset types
// This config drives the dynamic form rendering while maintaining exact UI

import {
  normalizeFrequency,
  normalizeInsuranceType,
  normalizeVehicleType,
  normalizeBlockchain
} from "../../../utils/assetFormUtils";

export const FIELD_CONFIGS = {
  insurance: {
    // UI customization
    ui: {
      notesPlaceholder: "e.g., Nominee details, claim process notes",
      tagsPlaceholder: "e.g., Family, Tax-saving"
    },
    // NOTE: Common fields for EDIT mode (skipped in ADD mode via GenericAssetForm conditional logic)
    // For ADD mode: AddAssets.js handles these fields, GenericAssetForm skips them
    // For EDIT mode: AssetFormWrapper renders these from config with insurance-specific labels
    commonFields: [
      {
        name: 'name',
        type: 'text',
        label: 'Policy Name',
        placeholder: 'e.g., AIA Health Plus, NIB Travel Cover',
        required: true,
        colClass: 'col-md-12 mb-4'
      },
      {
        name: 'purchaseDate',
        type: 'date',
        label: 'Policy Start Date',
        required: true,
        colClass: 'col-md-6 mb-4',
        showMonthDropdown: true,
        showYearDropdown: true,
        maxDate: new Date()
      },
      {
        name: 'purchasePrice',
        type: 'currencyWithSelect',
        label: 'Sum Assured / Coverage Amount',
        placeholder: 'e.g., 500,000',
        required: true,
        colClass: 'col-md-6 mb-4',
        currencyField: 'purchaseCurrency'
      }
    ],
    // Specific fields for insurance subcategory
    specificFields: [
      {
        name: 'insuranceType',
        type: 'select',
        label: 'Insurance Type',
        placeholder: 'Select insurance type',
        required: true,
        colClass: 'col-md-6 mb-4',
        options: [
          { value: "life", label: "Life Insurance" },
          { value: "term", label: "Term Insurance" },
          { value: "health", label: "Health Insurance" },
          { value: "home", label: "Home Insurance" },
          { value: "contents", label: "Contents Insurance" },
          { value: "vehicle", label: "Vehicle Insurance" },
          { value: "business", label: "Business Insurance" },
          { value: "travel", label: "Travel Insurance" },
          { value: "other", label: "Other Insurance" },
          // { value: "auto", label: "Auto Insurance" },
        ]
      },
      {
        name: 'providerName',
        type: 'text',
        label: 'Insurance Provider',
        placeholder: 'e.g., Southern Cross, NIB',
        required: true,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'policyNumber',
        type: 'text',
        label: 'Policy Number',
        placeholder: 'e.g., P123456789',
        required: true,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'policyHolderName',
        type: 'text',
        label: 'Policy Holder Name',
        placeholder: 'e.g., John Doe',
        required: true,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'premiumAmount',
        type: 'currency',
        label: 'Premium Amount',
        placeholder: 'e.g., 1,200',
        required: false,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'premiumFrequency',
        type: 'select',
        label: 'Premium Frequency',
        placeholder: 'Select frequency',
        required: false,
        colClass: 'col-md-6 mb-4',
        options: [
          { value: "monthly", label: "Monthly" },
          { value: "quarterly", label: "Quarterly" },
          { value: "half-yearly", label: "Half-Yearly" },
          { value: "yearly", label: "Yearly" },
          { value: "one-time", label: "One-Time" },
        ]
      },
      {
        name: 'maturityDate',
        type: 'date',
        label: 'Policy Expiry / Maturity Date',
        placeholder: 'Select expiry or maturity date',
        required: false,
        colClass: 'col-md-6 mb-4',
        minDate: 'purchaseDate', // References another field
        showMonthDropdown: true,
        showYearDropdown: true
      }
    ],
    // API configuration
    api: {
      detailsKey: 'insuranceDetails', // The key used in API payload
      detailsFields: [ // Fields that go inside insuranceDetails
        'insuranceType',
        'policyName',
        'policyNumber',
        'providerName',
        'policyHolderName',
        'sumAssured',
        'premiumAmount',
        'premiumFrequency',
        'policyStartDate',
        'maturityDate'
      ]
    },

    // Initial values generator for Add mode
    getInitialValues: (defaultCurrency = 'INR') => ({
      name: '',
      purchaseDate: new Date().toISOString().slice(0, 10),
      purchasePrice: '',
      purchaseCurrency: defaultCurrency,
      insuranceType: '',
      policyName: '',
      policyNumber: '',
      providerName: '',
      policyHolderName: '',
      sumAssured: '',
      premiumAmount: '',
      premiumFrequency: '',
      policyStartDate: '',
      maturityDate: '',
      notes: '',
      isActive: 'yes',
      assetCategory: 'digital',
      assetType: 'insurance',
      subCategory: 'insurance'  // Required by base validation
    }),

    // AI extraction field mapper
    extractionMapper: (extractedData) => {
      // Handle nested insuranceDetails structure
      const details = extractedData.insuranceDetails || {};

      return {
        providerName: details.providerName || extractedData.providerName || '',
        policyNumber: details.policyNumber || extractedData.policyNumber || '',
        name: details.policyName || extractedData.policyName || extractedData.name || '',
        insuranceType: normalizeInsuranceType(details.insuranceType || extractedData.insuranceType),
        policyHolderName: details.policyHolderName || extractedData.policyHolderName || '',
        purchasePrice: details.sumAssured || extractedData.sumAssured || extractedData.purchasePrice || '',
        premiumAmount: details.premiumAmount || extractedData.premiumAmount || '',
        premiumFrequency: normalizeFrequency(details.premiumFrequency || extractedData.premiumFrequency),
        purchaseDate: details.policyStartDate || extractedData.policyStartDate || extractedData.purchaseDate || '',
        maturityDate: details.maturityDate || details.policyEndDate || extractedData.policyEndDate || extractedData.expiryDate || extractedData.maturityDate || '',
        purchaseCurrency: extractedData.purchaseCurrency || undefined
      };
    },

    // Payload builder for API submission
    buildPayload: (values) => ({
      detailsKey: 'insuranceDetails',
      detailsData: {
        insuranceType: values.insuranceType,
        policyName: values.policyName,
        policyNumber: values.policyNumber,
        providerName: values.providerName,
        policyHolderName: values.policyHolderName,
        sumAssured: values.sumAssured || values.purchasePrice,
        premiumAmount: values.premiumAmount,
        premiumFrequency: values.premiumFrequency,
        policyStartDate: values.policyStartDate || values.purchaseDate,
        maturityDate: values.maturityDate,
        estimatedValue: values.purchasePrice
      }
    }),

    // Response mapper for Edit mode (API → Form)
    responseMapper: (apiData) => ({
      insuranceType: apiData.insuranceDetails?.insuranceType || '',
      policyName: apiData.insuranceDetails?.policyName || '',
      policyNumber: apiData.insuranceDetails?.policyNumber || '',
      providerName: apiData.insuranceDetails?.providerName || '',
      policyHolderName: apiData.insuranceDetails?.policyHolderName || '',
      sumAssured: apiData.insuranceDetails?.sumAssured || '',
      premiumAmount: apiData.insuranceDetails?.premiumAmount || '',
      premiumFrequency: apiData.insuranceDetails?.premiumFrequency || '',
      policyStartDate: apiData.insuranceDetails?.policyStartDate ? apiData.insuranceDetails.policyStartDate.slice(0, 10) : '',
      maturityDate: apiData.insuranceDetails?.maturityDate ? apiData.insuranceDetails.maturityDate.slice(0, 10) : ''
    })
  },

  // Vehicle Form Configuration
  vehicle: {
    ui: {
      notesPlaceholder: "e.g., New tyres in July 2025",
      tagsPlaceholder: "e.g., Personal, Leased"
    },
    // NOTE: Common fields for EDIT mode (skipped in ADD mode via GenericAssetForm conditional logic)
    // For ADD mode: AddAssets.js handles these fields, GenericAssetForm skips them
    // For EDIT mode: AssetFormWrapper renders these from config with vehicle-specific labels
    commonFields: [
      {
        name: 'name',
        type: 'text',
        label: 'Vehicle Name',
        placeholder: 'e.g., Family SUV, Office Van',
        required: true,
        colClass: 'col-md-12 mb-4'
      },
      {
        name: 'purchaseDate',
        type: 'date',
        label: 'Purchase Date',
        required: true,
        colClass: 'col-md-6 mb-4',
        showMonthDropdown: true,
        showYearDropdown: true,
        maxDate: new Date()
      },
      {
        name: 'purchasePrice',
        type: 'currencyWithSelect',
        label: 'Purchase Price',
        placeholder: 'e.g., 35,000',
        required: true,
        colClass: 'col-md-6 mb-4',
        currencyField: 'purchaseCurrency'
      }
    ],
    specificFields: [
      {
        name: 'vehicleType',
        type: 'select',
        label: 'Vehicle Type',
        required: true,
        colClass: 'col-md-6 mb-4',
        options: [
          { value: 'car', label: 'Car' },
          { value: 'motorcycle', label: 'Motorcycle' },
          { value: 'suv', label: 'SUV' },
          { value: 'truck', label: 'Truck' },
          { value: 'van', label: 'Van' },
          { value: 'electric', label: 'Electric Vehicle' },
          { value: 'other', label: 'Other' }
        ]
      },
      {
        name: 'manufacturingYear',
        type: 'text',
        label: 'Make & Model',
        placeholder: 'e.g., Tesla Model Y 2024',
        required: false,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'registrationNumber',
        type: 'text',
        label: 'Registration Number',
        placeholder: 'e.g., ABC-123',
        required: false,
        colClass: 'col-md-6 mb-4'
      },
      // {
      //   name: 'owners',
      //   type: 'text',
      //   label: 'Owner(s)',
      //   placeholder: 'e.g., John Doe',
      //   required: true,
      //   colClass: 'col-md-6 mb-4'
      // },
      {
        name: 'currentEstimateValue',
        type: 'currency',
        label: 'Estimated Current Value',
        placeholder: 'e.g., 28,000 (market value)',
        required: false,
        colClass: 'col-md-6 mb-4'
      }
    ],
    api: {
      detailsKey: 'vehicleDetails',
      detailsFields: [
        'vehicleType',
        'manufacturingYear',
        'registrationNumber',
        'owners',
        'currentEstimateValue'
      ]
    },

    // Initial values generator for Add mode
    getInitialValues: (defaultCurrency = 'INR') => ({
      name: '',
      purchaseDate: new Date().toISOString().slice(0, 10),
      purchasePrice: '',
      purchaseCurrency: defaultCurrency,
      vehicleType: '',
      manufacturingYear: '',
      registrationNumber: '',
      owners: '',
      currentEstimateValue: '',
      notes: '',
      isActive: 'yes',
      assetCategory: 'physical',
      assetType: 'vehicle',
      subCategory: 'vehicle'  // Required by base validation
    }),

    // AI extraction field mapper
    extractionMapper: (extractedData) => {
      console.log('🚗 Vehicle extraction mapper - Raw data:', extractedData);

      // Handle nested vehicleDetails structure
      const details = extractedData.vehicleDetails || {};

      // Handle owners field - could be array or string
      let ownersValue = details.owners || extractedData.owners || '';
      if (Array.isArray(ownersValue)) {
        ownersValue = ownersValue.join(', ');
      }

      const mappedValues = {
        name: details.vehicleName || extractedData.vehicleName || extractedData.name || '',
        vehicleType: normalizeVehicleType(details.vehicleType || extractedData.vehicleType),
        manufacturingYear: details.manufacturingYear || extractedData.manufacturingYear || '',
        registrationNumber: details.registrationNumber || extractedData.registrationNumber || '',
        owners: ownersValue,
        purchasePrice: extractedData.purchasePrice || '',
        estimatedValue: extractedData.estimatedValue || details.currentEstimateValue || '',
        currentEstimateValue: extractedData.estimatedValue || details.currentEstimateValue || '',
        purchaseCurrency: extractedData.purchaseCurrency || undefined,
        purchaseDate: extractedData.purchaseDate || ''
      };

      console.log('🚗 Vehicle extraction mapper - Mapped values:', mappedValues);
      return mappedValues;
    },

    // Payload builder for API submission
    buildPayload: (values) => ({
      detailsKey: 'vehicleDetails',
      detailsData: {
        vehicleName: values.name,
        vehicleType: values.vehicleType,
        manufacturingYear: values.manufacturingYear,
        registrationNumber: values.registrationNumber,
        owners: values.owners,
        currentEstimateValue: values.currentEstimateValue
      }
    }),

    // Response mapper for Edit mode (API → Form)
    responseMapper: (apiData) => ({
      vehicleType: apiData.vehicleDetails?.vehicleType || '',
      manufacturingYear: apiData.vehicleDetails?.manufacturingYear || '',
      registrationNumber: apiData.vehicleDetails?.registrationNumber || '',
      owners: apiData.vehicleDetails?.owners || '',
      currentEstimateValue: apiData.vehicleDetails?.currentEstimateValue || apiData.estimatedValue || ''
    })
  },

  // Cash Form Configuration
  cash: {
    // UI customization
    ui: {
      notesPlaceholder: "e.g., For Japan trip",
      tagsPlaceholder: "e.g., Travel, Savings"
    },
    commonFields: [
      {
        name: 'name',
        type: 'text',
        label: 'Cash Label',
        placeholder: 'e.g., Emergency Fund, Cash at Home',
        required: true,
        colClass: 'col-md-12 mb-4'
      },
      {
        name: 'purchaseDate',
        type: 'date',
        label: 'Date',
        required: true,
        colClass: 'col-md-6 mb-4',
        showMonthDropdown: true,
        showYearDropdown: true,
        maxDate: new Date()
      },
      {
        name: 'purchasePrice',
        type: 'currencyWithSelect',
        label: 'Amount',
        placeholder: 'e.g., 50,000',
        required: true,
        colClass: 'col-md-6 mb-4',
        currencyField: 'purchaseCurrency'
      }
    ],
    specificFields: [
      {
        name: 'storageLocation',
        type: 'text',
        label: 'Storage Location',
        placeholder: 'e.g., Home Safe, Wallet',
        required: false,
        colClass: 'col-md-12'
      }
    ],

    api: {
      detailsKey: 'cashDetails',
      detailsFields: [
        'cashLabel',
        'amount',
        'storageLocation'
      ]
    },

    // Initial values generator for Add mode
    getInitialValues: (defaultCurrency = 'INR') => ({
      name: '',
      purchaseDate: new Date().toISOString().slice(0, 10),
      purchasePrice: '',
      purchaseCurrency: defaultCurrency,
      storageLocation: '',
      notes: '',
      isActive: 'yes',
      assetCategory: 'physical',
      assetType: 'cash',
      subCategory: 'cash'  // Required by base validation
    }),

    // AI extraction field mapper
    extractionMapper: (extractedData) => ({
      name: extractedData.cashLabel || extractedData.name || '',
      purchasePrice: extractedData.amount || extractedData.purchasePrice || '',
      storageLocation: extractedData.storageLocation || '',
      purchaseCurrency: extractedData.purchaseCurrency || undefined
    }),

    // Payload builder for API submission
    buildPayload: (values) => ({
      detailsKey: 'cashDetails',
      detailsData: {
        cashLabel: values.name,
        amount: values.purchasePrice,
        storageLocation: values.storageLocation
      }
    }),

    // Response mapper for Edit mode (API → Form)
    responseMapper: (apiData) => ({
      storageLocation: apiData.cashDetails?.storageLocation || ''
    })
  },

  // Property Form Configuration
  property: {
    ui: {
      notesPlaceholder: "e.g., Renovated kitchen in Feb 2025",
      tagsPlaceholder: "e.g., Investment, Primary residence"
    },
    commonFields: [
      {
        name: 'name',
        type: 'text',
        label: 'Property Name',
        placeholder: 'e.g., Family Home, Rental Apartment',
        required: true,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'purchaseDate',
        type: 'date',
        label: 'Purchase Date',
        required: true,
        colClass: 'col-md-6 mb-4',
        showMonthDropdown: true,
        showYearDropdown: true,
        maxDate: new Date()
      },
      {
        name: 'purchasePrice',
        type: 'currencyWithSelect',
        label: 'Purchase Price',
        placeholder: 'e.g., 750,000',
        required: true,
        colClass: 'col-md-6 mb-4',
        currencyField: 'purchaseCurrency'
      }
    ],
    specificFields: [
      {
        name: 'propertyType',
        type: 'select',
        label: 'Property Type',
        required: true,
        colClass: 'col-md-6 mb-4',
        options: [
          { value: 'residential', label: 'Residential' },
          { value: 'commercial', label: 'Commercial' },
          { value: 'industrial', label: 'Industrial' },
          { value: 'land', label: 'Land' },
          { value: 'mixed-use', label: 'Mixed-use' }
        ]
      },
      {
        name: 'ownershipType',
        type: 'select',
        label: 'Ownership Type',
        required: true,
        colClass: 'col-md-6 mb-4',
        options: [
          { value: 'sole', label: 'Sole' },
          { value: 'joint', label: 'Joint' },
          { value: 'trust', label: 'Trust' },
          { value: 'company', label: 'Company' },
          { value: 'partnership', label: 'Partnership' },
          { value: 'inherited', label: 'Inherited' },
          { value: 'other', label: 'Other' }
        ]
      },
      {
        name: 'rentalIncome.amount',
        type: 'currency',
        label: 'Rental Income',
        placeholder: 'e.g., 2,500',
        required: false,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'rentalIncome.frequency',
        type: 'select',
        label: 'Rental Frequency',
        required: false,
        colClass: 'col-md-6 mb-4',
        options: [
          { value: 'weekly', label: 'Weekly' },
          { value: 'fortnightly', label: 'Fortnightly' },
          { value: 'monthly', label: 'Monthly' },
          { value: 'quarterly', label: 'Quarterly' },
          { value: 'halfyearly', label: 'Half-Yearly' },
          { value: 'yearly', label: 'Yearly' }
        ]
      },
      {
        name: 'ownershipPercentage',
        type: 'text',
        label: 'Ownership Percentage',
        placeholder: 'e.g., 100% ',
        required: true,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'propertyAddress',
        type: 'text',
        label: 'Property Address',
        placeholder: 'e.g., 123 Queen Street, Auckland Central, Auckland, New Zealand',
        required: true,
        colClass: 'col-md-12'
      }
    ],
    api: {
      detailsKey: 'propertyDetails',
      detailsFields: [
        'propertyType',
        'ownershipType',
        'rentalIncome',
        'ownershipPercentage',
        'propertyAddress'
      ]
    },

    // Initial values generator for Add mode
    getInitialValues: (defaultCurrency = 'INR') => ({
      name: '',
      purchaseDate: new Date().toISOString().slice(0, 10),
      purchasePrice: '',
      purchaseCurrency: defaultCurrency,
      propertyType: '',
      ownershipType: '',
      'rentalIncome.amount': '',
      'rentalIncome.frequency': '',
      ownershipPercentage: '',
      propertyAddress: '',
      notes: '',
      isActive: 'yes',
      assetCategory: 'physical',
      assetType: 'property',
      subCategory: 'property'  // Required by base validation
    }),

    // AI extraction field mapper
    extractionMapper: (extractedData) => {
      // Handle both flat and nested structures (propertyDetails)
      const details = extractedData.propertyDetails || {};

      // Handle rental income - could be object or number with separate frequency field
      let rentalAmount = '';
      let rentalFrequency = '';

      // Check nested propertyDetails first
      if (details.rentalIncome) {
        if (typeof details.rentalIncome === 'object') {
          rentalAmount = details.rentalIncome.amount || '';
          rentalFrequency = details.rentalIncome.frequency || '';
        } else {
          rentalAmount = details.rentalIncome;
          rentalFrequency = details.rentalFrequency || '';
        }
      }
      // Fallback to top-level fields
      else if (extractedData.rentalIncome) {
        if (typeof extractedData.rentalIncome === 'object') {
          rentalAmount = extractedData.rentalIncome.amount || '';
          rentalFrequency = extractedData.rentalIncome.frequency || '';
        } else {
          rentalAmount = extractedData.rentalIncome;
          rentalFrequency = extractedData.rentalFrequency || '';
        }
      }

      // Normalize propertyType to lowercase for select field matching
      const rawPropertyType = details.propertyType || extractedData.propertyType || '';
      let propertyType = rawPropertyType.toLowerCase();

      // Map common variations to valid options
      const propertyTypeMap = {
        'residential apartment': 'residential',
        'residential house': 'residential',
        'apartment': 'residential',
        'house': 'residential',
        'flat': 'residential',
        'commercial building': 'commercial',
        'commercial property': 'commercial',
        'office': 'commercial',
        'shop': 'commercial',
        'industrial property': 'industrial',
        'factory': 'industrial',
        'warehouse': 'industrial',
        'plot': 'land',
        'vacant land': 'land',
        'mixed': 'mixeduse',
        'mixed-use': 'mixeduse'
      };
      propertyType = propertyTypeMap[propertyType] || propertyType;

      // Normalize ownershipType to lowercase
      const rawOwnershipType = details.ownershipType || extractedData.ownershipType || '';
      let ownershipType = rawOwnershipType.toLowerCase();

      // Map common variations to valid options
      const ownershipTypeMap = {
        'self-owned': 'sole',
        'self owned': 'sole',
        'single': 'sole',
        'individual': 'sole',
        'co-owned': 'joint',
        'co owned': 'joint',
        'shared': 'joint'
      };
      ownershipType = ownershipTypeMap[ownershipType] || ownershipType;

      return {
        name: details.propertyName || extractedData.propertyName || extractedData.name || '',
        propertyType: propertyType,
        ownershipType: ownershipType,
        'rentalIncome.amount': rentalAmount,
        'rentalIncome.frequency': normalizeFrequency(rentalFrequency),
        ownershipPercentage: extractedData.estimatedValue || details.estimatedValue || extractedData.estimatedCurrentValue || details.estimatedCurrentValue || extractedData.ownershipPercentage || details.ownershipPercentage || '',
        propertyAddress: details.propertyAddress || extractedData.propertyAddress || '',
        purchasePrice: extractedData.purchasePrice || details.purchasePrice || '',
        purchaseCurrency: extractedData.purchaseCurrency || details.purchaseCurrency || undefined,
        purchaseDate: extractedData.purchaseDate || details.purchaseDate || ''
      };
    },

    // Payload builder for API submission
    buildPayload: (values) => {
      const detailsData = {
        propertyName: values.name,
        propertyType: values.propertyType,
        rentalIncome: {
          amount: values['rentalIncome.amount'] || '',
          frequency: values['rentalIncome.frequency'] || ''
        },
        ownershipPercentage: values.ownershipPercentage,
        propertyAddress: values.propertyAddress,
      };
      // Only include ownershipType if it has a non-empty value
      if (values.ownershipType && values.ownershipType.trim().length > 0) {
        detailsData.ownershipType = values.ownershipType;
      }
      return {
        detailsKey: 'propertyDetails',
        detailsData
      };
    },

    // Response mapper for Edit mode (API → Form)
    responseMapper: (apiData) => ({
      propertyType: apiData.propertyDetails?.propertyType || '',
      ownershipType: apiData.propertyDetails?.ownershipType || '',
      'rentalIncome.amount': apiData.propertyDetails?.rentalIncome?.amount || '',
      'rentalIncome.frequency': apiData.propertyDetails?.rentalIncome?.frequency || '',
      ownershipPercentage: apiData.propertyDetails?.ownershipPercentage || '',
      propertyAddress: apiData.propertyDetails?.propertyAddress || '',
      estimatedCurrentValue: apiData.propertyDetails?.estimatedCurrentValue || apiData.estimatedValue || ''
    })
  },

  // Bank Accounts Form Configuration
  "bank accounts": {
    ui: {
      notesPlaceholder: "e.g., Salary account, Joint account with spouse",
      tagsPlaceholder: "e.g., Salary, Savings"
    },
    commonFields: [
      {
        name: 'name',
        type: 'text',
        label: 'Bank Name',
        placeholder: 'e.g., ANZ Bank, Commonwealth Bank',
        required: true,
        colClass: 'col-md-12 mb-4'
      },
      {
        name: 'purchaseDate',
        type: 'date',
        label: 'Account Opening Date',
        required: true,
        colClass: 'col-md-6 mb-4',
        showMonthDropdown: true,
        showYearDropdown: true,
        maxDate: new Date()
      },
      {
        name: 'purchasePrice',
        type: 'currencyWithSelect',
        label: 'Current Balance',
        placeholder: 'e.g., 50,000',
        required: true,
        colClass: 'col-md-6 mb-4',
        currencyField: 'purchaseCurrency'
      }
    ],
    specificFields: [
      {
        name: 'accountName',
        type: 'text',
        label: 'Account Name',
        placeholder: 'e.g., Salary Account, Joint Savings',
        required: false,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'accountType',
        type: 'select',
        label: 'Account Type',
        required: true,
        placeholder: 'Select account type',
        colClass: 'col-md-6 mb-4',
        options: [
          { value: 'savings', label: 'Savings' },
          { value: 'checking', label: 'Checking' },
          { value: 'current', label: 'Current' },
          { value: 'termDeposit', label: 'Term Deposit' },
          { value: 'other', label: 'Other' },
          // { value: 'nro', label: 'NRO' },
          // { value: 'nre', label: 'NRE' },
        ]
      },
      {
        name: 'accountNumber',
        type: 'text',
        label: 'Account Number',
        placeholder: 'e.g., 12-3456-0789123-00',
        required: true,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'ownershipType',
        type: 'select',
        label: 'Ownership Type',
        required: true,
        placeholder: 'Select ownership type',
        colClass: 'col-md-6 mb-4',
        options: [
          { value: 'sole', label: 'Sole' },
          { value: 'joint', label: 'Joint' },
          { value: 'business', label: 'Business' },
          { value: 'trust', label: 'Trust' },
          { value: 'other', label: 'Other' },
          // { value: 'leased', label: 'Leased' }
        ]
      },
      {
        name: 'country',
        type: 'countrySelect',
        label: 'Country',
        required: false,
        placeholder: 'Select country',
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'bankLocation',
        type: 'text',
        label: 'Bank Location',
        placeholder: 'e.g., 1 Queen St, Auckland CBD',
        required: false,
        colClass: 'col-md-6 mb-4'
      }
    ],
    api: {
      detailsKey: 'bankAccountDetails',
      detailsFields: [
        'accountName',
        'accountType',
        'accountNumber',
        'ownershipType',
        'country',
        'bankLocation'
      ]
    },

    // Initial values generator for Add mode
    getInitialValues: (defaultCurrency = 'INR') => ({
      name: '',
      purchaseDate: new Date().toISOString().slice(0, 10),
      purchasePrice: '',
      purchaseCurrency: defaultCurrency,
      accountName: '',
      accountType: '',
      accountNumber: '',
      ownershipType: '',
      country: '',
      bankLocation: '',
      notes: '',
      isActive: 'yes',
      assetCategory: 'digital',
      assetType: 'bankAccounts',
      subCategory: 'bankAccounts'  // Required by base validation
    }),

    // AI extraction field mapper
    extractionMapper: (extractedData) => {

      // Handle nested bankAccountDetails structure
      const details = extractedData.bankAccountDetails || {};

      // Clean up account type - remove extra text in parentheses
      let accountType = details.accountType || extractedData.accountType || '';
      if (accountType.includes('(')) {
        accountType = accountType.split('(')[0].trim();
      }
      accountType = accountType.toLowerCase();

      // Map account type variations to valid options
      const accountTypeMap = {
        'savings account': 'savings',
        'savings': 'savings',
        'checking account': 'checking',
        'checking': 'checking',
        'current account': 'current',
        'current': 'current',
        'nro': 'nro',
        'nre': 'nre'
      };

      accountType = accountTypeMap[accountType] || accountType;

      return {
        name: details.providerName || extractedData.bankName || extractedData.name || '',
        accountName: details.accountName || extractedData.accountName || '',
        accountType: accountType,
        accountNumber: details.accountNumber || extractedData.accountNumber || '',
        ownershipType: (details.ownershipType || extractedData.ownershipType || '').toLowerCase(),
        country: details.country || extractedData.country || '',
        bankLocation: details.bankLocation || extractedData.bankLocation || extractedData.location || '',
        purchasePrice: extractedData.currentBalance || extractedData.balance || extractedData.purchasePrice || '',
        purchaseCurrency: extractedData.currency || extractedData.purchaseCurrency || undefined,
        purchaseDate: extractedData.accountOpeningDate || extractedData.openingDate || extractedData.purchaseDate || ''
      };
    },

    // Payload builder for API submission
    buildPayload: (values) => ({
      detailsKey: 'bankAccountDetails',
      detailsData: {
        bankName: values.name,
        accountName: values.accountName,
        accountType: values.accountType,
        accountNumber: values.accountNumber,
        ownershipType: values.ownershipType,
        country: values.country,
        bankLocation: values.bankLocation,
        currentBalance: values.purchasePrice
      }
    }),

    // Response mapper for Edit mode (API → Form)
    responseMapper: (apiData) => ({
      accountName: apiData.bankAccountDetails?.accountName || '',
      accountType: apiData.bankAccountDetails?.accountType || '',
      accountNumber: apiData.bankAccountDetails?.accountNumber || '',
      ownershipType: apiData.bankAccountDetails?.ownershipType || '',
      country: apiData.bankAccountDetails?.country || '',
      bankLocation: apiData.bankAccountDetails?.bankLocation || ''
    })
  },

  // Mutual Funds Form Configuration
  "mutual funds": {
    // UI customization
    ui: {
      notesPlaceholder: "e.g., Long-term investment, SIP for retirement",
      tagsPlaceholder: "e.g., Retirement, Long-term"
    },
    commonFields: [
      {
        name: 'name',
        type: 'text',
        label: 'Fund Name',
        placeholder: 'e.g., HDFC Equity Fund, Vanguard 500 Index Fund',
        required: true,
        colClass: 'col-md-12 mb-4'
      },
      {
        name: 'purchaseDate',
        type: 'date',
        label: 'Investment Start Date',
        required: true,
        colClass: 'col-md-6 mb-4',
        showMonthDropdown: true,
        showYearDropdown: true,
        maxDate: new Date()
      },
      {
        name: 'purchasePrice',
        type: 'currencyWithSelect',
        label: 'Total Invested Amount',
        placeholder: 'e.g., 50,000',
        required: true,
        colClass: 'col-md-6 mb-4',
        currencyField: 'purchaseCurrency'
      }
    ],
    specificFields: [
      {
        name: 'fundType',
        type: 'text',
        label: 'Fund Type',
        placeholder: 'e.g., Equity – Large Cap, S&P 500 Index',
        required: false,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'providerName',
        type: 'text',
        label: 'Platform/Provider',
        placeholder: 'e.g., Vanguard, Groww',
        required: true,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'folioNumber',
        type: 'text',
        label: 'Folio Number',
        placeholder: 'e.g., MF-456789',
        required: true,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'currentEstimateValue',
        type: 'currency',
        label: 'Estimated Current Value ',
        placeholder: 'e.g., 65,000',
        required: true,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'units',
        type: 'number',
        label: 'Unit Held',
        placeholder: 'e.g., 150.75',
        required: true,
        colClass: 'col-md-6 mb-4'
      }
    ],

    api: {
      detailsKey: 'mutualFundDetails',
      detailsFields: [
        'fundType',
        'providerName',
        'folioNumber',
        'currentEstimateValue',
        'units'
      ]
    },

    // Initial values generator for Add mode
    getInitialValues: (defaultCurrency = 'INR') => ({
      name: '',
      purchaseDate: new Date().toISOString().slice(0, 10),
      purchasePrice: '',
      purchaseCurrency: defaultCurrency,
      fundType: '',
      providerName: '',
      folioNumber: '',
      currentEstimateValue: '',
      units: '',
      notes: '',
      isActive: 'yes',
      assetCategory: 'digital',
      assetType: 'mutual funds',
      subCategory: 'mutual funds'  // Required by base validation
    }),

    // AI extraction field mapper
    extractionMapper: (extractedData) => {

      // Handle both flat and nested structures
      const details = extractedData.mutualFundDetails || {};

      return {
        name: details.planName || extractedData.planName || extractedData.fundName || extractedData.name || '',
        fundType: details.fundType || extractedData.fundType || '',
        providerName: details.providerName || extractedData.providerName || '',
        folioNumber: details.folioNumber || extractedData.folioNumber || '',
        currentEstimateValue: extractedData.estimatedValue || details.currentEstimateValue || extractedData.currentEstimateValue || '',
        units: details.units || extractedData.units || '',
        purchasePrice: extractedData.purchasePrice || extractedData.investedAmount || '',
        purchaseCurrency: extractedData.purchaseCurrency || undefined,
        purchaseDate: extractedData.purchaseDate || ''
      };
    },

    // Payload builder for API submission
    buildPayload: (values) => ({
      detailsKey: 'mutualFundDetails',
      detailsData: {
        fundName: values.name,
        fundType: values.fundType,
        providerName: values.providerName,
        folioNumber: values.folioNumber,
        currentEstimateValue: values.currentEstimateValue,
        units: values.units
      }
    }),

    // Response mapper for Edit mode (API → Form)
    responseMapper: (apiData) => ({
      fundType: apiData.mutualFundDetails?.fundType || '',
      providerName: apiData.mutualFundDetails?.providerName || '',
      folioNumber: apiData.mutualFundDetails?.folioNumber || '',
      currentEstimateValue: apiData.mutualFundDetails?.currentEstimateValue || '',
      units: apiData.mutualFundDetails?.units || ''
    })
  },

  // Fixed Deposit configuration
  'fixed deposit': {
    // UI customization
    ui: {
      notesPlaceholder: "e.g., Auto-renews on maturity, Joint FD with spouse",
      tagsPlaceholder: "e.g., Low-risk, Emergency"
    },
    commonFields: [
      {
        name: 'name',
        type: 'text',
        label: 'Bank/Institution Name',
        placeholder: 'e.g., ANZ Bank, Commonwealth Bank"',
        required: true,
        colClass: 'col-md-12 mb-4'
      },
      {
        name: 'depositLabel',
        type: 'text',
        label: 'Deposit Label',
        placeholder: 'e.g., Emergency FD, Retirement FD',
        required: false,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'accountNumber',
        type: 'text',
        label: 'Account/Reference Number',
        placeholder: 'e.g., FD-0098123',
        required: true,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'purchaseDate',
        type: 'date',
        label: 'Start Date',
        required: true,
        colClass: 'col-md-6 mb-4',
        showMonthDropdown: true,
        showYearDropdown: true,
        maxDate: new Date()
      },
      {
        name: 'maturityDate',
        type: 'date',
        label: 'Maturity Date',
        placeholder: 'Select maturity date',
        required: true,
        colClass: 'col-md-6 mb-4',
        showMonthDropdown: true,
        showYearDropdown: true,
        minDate: 'purchaseDate'
      },
      {
        name: 'purchasePrice',
        type: 'currencyWithSelect',
        label: 'Deposit Amount',
           placeholder: 'e.g., 150,000',
        required: true,
        colClass: 'col-md-6 mb-4',
        currencyField: 'purchaseCurrency'
      },
      {
        name: 'interestRate',
        type: 'number',
        label: 'Interest Rate',
        placeholder: 'e.g., 7.5%',
        required: true,
        colClass: 'col-md-6 mb-4'
      }
    ],
    specificFields: [
      {
        name: 'currentEstimateValue',
        type: 'currency',
        label: 'Estimated Current Value',
           placeholder: 'e.g., 152,500',
        required: false,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'maturityAmount',
        type: 'currency',
        label: 'Maturity Amount',
           placeholder: 'e.g., 210,000',
        required: true,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'ownershipType',
        type: 'select',
        label: 'Ownership Type',
        placeholder: 'Select ownership type',
        required: false,
        colClass: 'col-md-6 mb-4',
        options: [
          { value: 'soleOwnership', label: 'Sole Ownership' },
          { value: 'jointOwnership', label: 'Joint Ownership' },
          { value: 'jointSpousePartner', label: 'Joint with Spouse/Partner' },
          { value: 'familyOwnership', label: 'Family Ownership' },
          { value: 'companyBusinessOwned', label: 'Company/Business Owned' },
          { value: 'trustOwned', label: 'Trust Owned' },
          { value: 'inherited', label: 'Inherited' },
          { value: 'other', label: 'Other' }
        ]
      },
      {
        name: 'interestPayoutFrequency',
        type: 'select',
        label: 'Interest Payout Frequency',
        placeholder: 'Select frequency',
        required: false,
        colClass: 'col-md-6 mb-4',
        options: [
          { value: 'monthly', label: 'Monthly' },
          { value: 'quarterly', label: 'Quarterly' },
          { value: 'halfYearly', label: 'Half-Yearly' },
          { value: 'annually', label: 'Annually' },
          { value: 'atMaturity', label: 'At Maturity' },
          { value: 'notApplicable', label: 'Not Applicable' }
        ]
      }
    ],
    api: {
      detailsKey: 'fixedDepositDetails',
      detailsFields: [
        'depositLabel',
        'accountNumber',
        'maturityDate',
        'interestRate',
        'currentEstimateValue',
        'maturityAmount',
        'ownershipType',
        'interestPayoutFrequency'
      ]
    },

    // Initial values generator for Add mode
    getInitialValues: (defaultCurrency = 'INR') => ({
      name: '',
      accountNumber: '',
      purchaseDate: new Date().toISOString().slice(0, 10),
      maturityDate: '',
      purchasePrice: '',
      purchaseCurrency: defaultCurrency,
      interestRate: '',
      currentEstimateValue: '',
      maturityAmount: '',
      ownershipType: '',
      interestPayoutFrequency: '',
      notes: '',
      isActive: 'yes',
      assetCategory: 'digital',
      assetType: 'fixed deposit',
      subCategory: 'fixed deposit'  // Required by base validation
    }),

    // AI extraction field mapper
    extractionMapper: (extractedData) => {

      // Handle nested fixedDepositDetails structure
      const details = extractedData.fixedDepositDetails || {};

      // Helper function to format date from AI response
      const formatDate = (dateStr) => {
        if (!dateStr) return '';
        try {
          const date = new Date(dateStr);
          if (isNaN(date.getTime())) return '';
          return date.toISOString().slice(0, 10);
        } catch (e) {
          console.warn('Invalid date format:', dateStr);
          return '';
        }
      };

      return {
        name: details.depositLabel || extractedData.depositLabel || extractedData.name || 'Fixed Deposit',
        accountNumber: details.accountNumber || extractedData.accountNumber || '',
        purchaseDate: formatDate(extractedData.purchaseDate) || formatDate(details.startDate) || formatDate(extractedData.startDate) || '',
        maturityDate: formatDate(details.maturityDate) || formatDate(extractedData.maturityDate) || '',
        purchasePrice: extractedData.purchasePrice || details.depositAmount || extractedData.depositAmount || '',
        interestRate: details.interestRate || extractedData.interestRate || '',
        currentEstimateValue: extractedData.estimatedValue || details.currentEstimateValue || extractedData.currentEstimateValue || '',
        maturityAmount: details.maturityAmount || extractedData.maturityAmount || '',
        purchaseCurrency: extractedData.purchaseCurrency || undefined
      };
    },

    // Payload builder for API submission
    buildPayload: (values) => ({
      detailsKey: 'fixedDepositDetails',
      detailsData: {
        depositLabel: values.name,
        accountNumber: values.accountNumber,
        maturityDate: values.maturityDate,
        interestRate: values.interestRate,
        currentEstimateValue: values.currentEstimateValue,
        maturityAmount: values.maturityAmount
      }
    }),

    // Response mapper for Edit mode (API → Form)
    responseMapper: (apiData) => ({
      accountNumber: apiData.fixedDepositDetails?.accountNumber || '',
      maturityDate: apiData.fixedDepositDetails?.maturityDate ? apiData.fixedDepositDetails.maturityDate.slice(0, 10) : '',
      interestRate: apiData.fixedDepositDetails?.interestRate || '',
      currentEstimateValue: apiData.fixedDepositDetails?.currentEstimateValue || '',
      maturityAmount: apiData.fixedDepositDetails?.maturityAmount || ''
    })
  },

  // Stocks configuration
  'stocks': {
    // UI customization
    ui: {
      notesPlaceholder: "e.g., Long-term investment, Dividend stock",
      tagsPlaceholder: "e.g., Tech, Dividend"
    },
    commonFields: [
      {
        name: 'name',
        type: 'text',
        label: 'Stock Name',
        placeholder: 'e.g., Apple Inc, Tesla Inc',
        required: true,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'stockTicker',
        type: 'text',
        label: 'Stock Ticker',
        placeholder: 'e.g., AAPL, TSLA',
        required: true,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'brokerPlatform',
        type: 'text',
        label: 'Broker/Platform',
        placeholder: 'e.g., Sharesies, Robinhood',
        required: true,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'purchaseDate',
        type: 'date',
        label: 'Purchase Date',
        required: true,
        colClass: 'col-md-6 mb-4',
        showMonthDropdown: true,
        showYearDropdown: true,
        maxDate: new Date()
      },
      {
        name: 'purchasePrice',
        type: 'currencyWithSelect',
        label: 'Average Purchase Price',
        placeholder: 'e.g., 150',
        required: true,
        colClass: 'col-md-6 mb-4',
        currencyField: 'purchaseCurrency'
      },
      {
        name: 'numberOfShares',
        type: 'number',
        label: 'Total Shares Owned',
        placeholder: 'e.g., 10',
        required: true,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'currentMarketPrice',
        type: 'currency',
        label: 'Current Market Price',
        placeholder: 'e.g., 215',
        required: true,
        colClass: 'col-md-6 mb-4',
        hideInAdd: true
      }
    ],
    specificFields: [
      {
        name: 'estimatedValue',
        type: 'currency',
        label: 'Estimated Value',
        placeholder: 'e.g., 2150',
        required: true,
        colClass: 'col-md-6 mb-4',
        currencyField: 'purchaseCurrency'
      },
      {
        name: 'dividendPaying',
        type: 'radio',
        label: 'Dividend Paying',
        required: false,
        colClass: 'col-md-6 mb-4',
        options: [
          { value: "yes", label: "Yes" },
          { value: "no", label: "No" }
        ]
      }
    ],
    api: {
      detailsKey: 'stockDetails',
      detailsFields: [
        'stockTicker',
        'brokerPlatform',
        'numberOfShares',
        'currentMarketPrice',
        'estimatedValue',
        'dividendPaying'
      ]
    },

    // Initial values generator for Add mode
    getInitialValues: (defaultCurrency = 'INR') => ({
      name: '',
      purchaseDate: new Date().toISOString().slice(0, 10),
      purchasePrice: '',
      purchaseCurrency: defaultCurrency,
      stockTicker: '',
      brokerPlatform: '',
      numberOfShares: '',
      currentMarketPrice: '',
      estimatedValue: '',
      dividendPaying: 'no',
      notes: '',
      isActive: 'yes',
      assetCategory: 'digital',
      assetType: 'stocks',
      subCategory: 'stocks'  // Required by base validation
    }),

    // AI extraction field mapper
    extractionMapper: (extractedData) => {

      // Handle nested stockDetails structure (can be array or single object)
      const details = extractedData.stockDetails || {};

      // If stockDetails is an array, take the first item for single-item forms
      // (Multiple items are handled by the picker modal in AddAssets.js)
      const firstDetail = Array.isArray(details) ? details[0] : details;

      return {
        name: firstDetail.stockName || extractedData.stockName || extractedData.name || '',
        stockTicker: firstDetail.stockTicker || extractedData.stockTicker || '',
        numberOfShares: firstDetail.numberOfShares || extractedData.numberOfShares || '',
        purchasePrice: extractedData.purchasePrice || firstDetail.averageBuyPrice || extractedData.averageBuyPrice || '',
        currentMarketPrice: firstDetail.currentPrice || firstDetail.currentMarketPrice || extractedData.currentPrice || extractedData.currentMarketPrice || '',
        estimatedValue: extractedData.estimatedValue || firstDetail.estimatedValue || '',
        brokerPlatform: firstDetail.brokerPlatform || extractedData.brokerPlatform || '',
        purchaseCurrency: extractedData.purchaseCurrency || undefined,
        purchaseDate: extractedData.purchaseDate || ''
      };
    },

    // Payload builder for API submission
    buildPayload: (values) => ({
      detailsKey: 'stockDetails',
      detailsData: {
        stockName: values.name,
        stockTicker: values.stockTicker,
        brokerPlatform: values.brokerPlatform,
        numberOfShares: values.numberOfShares,
        currentMarketPrice: values.currentMarketPrice,
        estimatedValue: values.estimatedValue,
        dividendPaying: values.dividendPaying === "yes"
      }
    }),

    // Response mapper for Edit mode (API → Form)
    responseMapper: (apiData) => ({
      stockTicker: apiData.stockDetails?.stockTicker || '',
      brokerPlatform: apiData.stockDetails?.brokerPlatform || '',
      numberOfShares: apiData.stockDetails?.numberOfShares || '',
      currentMarketPrice: apiData.stockDetails?.currentMarketPrice || '',
      estimatedValue: apiData.stockDetails?.estimatedValue || apiData.estimatedValue || '',
      dividendPaying: apiData.stockDetails?.dividendPaying ? "yes" : "no"
    })
  },

  // Crypto configuration
  'crypto': {
    // UI customization
    ui: {
      notesPlaceholder: "e.g., Long-term hold, Cold storage",
      tagsPlaceholder: "e.g., Long-term, DeFi"
    },
    commonFields: [
      {
        name: 'name',
        type: 'text',
        label: 'Coin Name',
        placeholder: 'e.g., Bitcoin, Ethereum',
        required: true,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'cryptoTicker',
        type: 'text',
        label: 'Coin Symbol',
        placeholder: 'e.g., BTC, ETH',
        required: true,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'walletExchange',
        type: 'text',
        label: 'Wallet or Exchange',
        placeholder: 'e.g., Binance, CoinDCX',
        required: true,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'purchaseDate',
        type: 'date',
        label: 'Purchase Date',
        required: true,
        colClass: 'col-md-6 mb-4',
        showMonthDropdown: true,
        showYearDropdown: true,
        maxDate: new Date()
      },
      {
        name: 'purchasePrice',
        type: 'currencyWithSelect',
        label: 'Purchase Price',
        placeholder: 'e.g., 45,000',
        required: true,
        colClass: 'col-md-6 mb-4',
        currencyField: 'purchaseCurrency'
      },
      {
        name: 'numberOfCoins',
        type: 'number',
        label: 'Number of Coins',
        placeholder: 'e.g., 10.22',
        required: true,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'totalOwned',
        type: 'number',
        label: 'Total Owned',
        placeholder: 'e.g., 0.75',
        required: true,
        colClass: 'col-md-6 mb-4'
      }
    ],
    specificFields: [
      {
        name: 'currentMarketPrice',
        type: 'currency',
        label: 'Current Market Price',
        placeholder: 'e.g., 52,000',
        required: true,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'estimatedCurrentValue',
        type: 'currency',
        label: 'Estimated Current Value',
        placeholder: 'e.g., 55,000',
        required: true,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'networkBlockchain',
        type: 'select',
        label: 'Network/Blockchain',
        required: true,
        colClass: 'col-md-6 mb-4',
        placeholder: 'Select network',
        options: [
          { value: 'bitcoin', label: 'Bitcoin' },
          { value: 'ethereum', label: 'Ethereum' },
          { value: 'solana', label: 'Solana' },
          { value: 'polygon', label: 'Polygon' },
          { value: 'bsc', label: 'BSC (Binance Smart Chain)' },
          { value: 'avalanche', label: 'Avalanche' }
        ]
      },
      {
        name: 'walletAddress',
        type: 'text',
        label: 'Wallet Address',
        placeholder: 'e.g., bc1qxyz…',
        required: true,
        colClass: 'col-md-12'
      }
    ],
    api: {
      detailsKey: 'cryptoDetails',
      detailsFields: [
        'cryptoTicker',
        'walletExchange',
        'numberOfCoins',
        'totalOwned',
        'currentMarketPrice',
        'networkBlockchain',
        'walletAddress'
      ]
    },

    // Initial values generator for Add mode
    getInitialValues: (defaultCurrency = 'INR') => ({
      name: '',
      walletExchange: '',
      purchaseDate: new Date().toISOString().slice(0, 10),
      purchasePrice: '',
      purchaseCurrency: defaultCurrency,
      numberOfCoins: '',
      totalOwned: '',
      currentMarketPrice: '',
      networkBlockchain: '',
      walletAddress: '',
      notes: '',
      isActive: 'yes',
      assetCategory: 'digital',
      assetType: 'crypto',
      subCategory: 'crypto'  // Required by base validation
    }),

    // AI extraction field mapper
    extractionMapper: (extractedData) => {
  
      // Handle nested cryptoDetails structure (can be array or single object)
      const details = extractedData.cryptoDetails || {};

      // If cryptoDetails is an array, take the first item for single-item forms
      // (Multiple items are handled by the picker modal in AddAssets.js)
      const firstDetail = Array.isArray(details) ? details[0] : details;

      return {
        name: firstDetail.cryptoName || extractedData.cryptoName || extractedData.coinSymbol || extractedData.name || '',
        cryptoTicker: firstDetail.cryptoTicker || extractedData.cryptoTicker || extractedData.coinSymbol || '',
        walletExchange: firstDetail.walletExchange || firstDetail.platform || extractedData.walletExchange || extractedData.platform || '',
        numberOfCoins: firstDetail.numberOfCoins || extractedData.numberOfCoins || '',
        totalOwned: firstDetail.totalOwned || extractedData.totalOwned || '',
        currentMarketPrice: firstDetail.currentMarketPrice || firstDetail.averageBuyPrice || extractedData.currentMarketPrice || '',
        networkBlockchain: normalizeBlockchain(firstDetail.networkBlockchain || extractedData.networkBlockchain),
        walletAddress: firstDetail.walletAddress || extractedData.walletAddress || '',
        purchasePrice: extractedData.purchasePrice || '',
        purchaseCurrency: extractedData.purchaseCurrency || undefined,
        purchaseDate: extractedData.purchaseDate || ''
      };
    },

    // Payload builder for API submission
    buildPayload: (values) => ({
      detailsKey: 'cryptoDetails',
      detailsData: {
        cryptoTicker: values.name,
        walletExchange: values.walletExchange,
        numberOfCoins: values.numberOfCoins,
        totalOwned: values.totalOwned,
        currentMarketPrice: values.currentMarketPrice,
        networkBlockchain: values.networkBlockchain,
        walletAddress: values.walletAddress,
        estimatedCurrentValue: values.estimatedCurrentValue
      }
    }),

    // Response mapper for Edit mode (API → Form)
    responseMapper: (apiData) => ({
      walletExchange: apiData.cryptoDetails?.walletExchange || '',
      numberOfCoins: apiData.cryptoDetails?.numberOfCoins || '',
      totalOwned: apiData.cryptoDetails?.totalOwned || '',
      currentMarketPrice: apiData.cryptoDetails?.currentMarketPrice || '',
      networkBlockchain: apiData.cryptoDetails?.networkBlockchain || '',
      walletAddress: apiData.cryptoDetails?.walletAddress || '',
      estimatedCurrentValue: apiData.cryptoDetails?.estimatedCurrentValue || apiData.estimatedValue || ''
    })
  },

  // Collectibles configuration
  'collectibles': {
    ui: {
      notesPlaceholder: "e.g., Restoration history",
      tagsPlaceholder: "e.g., Limited Edition, Investment"
    },
    commonFields: [
      {
        name: 'name',
        type: 'text',
        label: 'Collectible Name',
        placeholder: 'e.g., Vintage Rolex Watch, Art Painting',
        required: true,
        colClass: 'col-md-12 mb-4'
      },
      {
        name: 'categoryType',
        type: 'select',
        label: 'Category / Type',
        required: true,
        colClass: 'col-md-6 mb-4',
        options: [
          { value: 'art', label: 'Art' },
          { value: 'watch', label: 'Watch' },
          { value: 'stamp', label: 'Stamp' },
          { value: 'wine', label: 'Wine' },
          { value: 'antique', label: 'Antique' },
          { value: 'memorabilia', label: 'Memorabilia' },
          { value: 'other', label: 'Other' }
        ]
      },
      {
        name: 'purchaseDate',
        type: 'date',
        label: 'Purchase Date',
        required: true,
        colClass: 'col-md-6 mb-4',
        showMonthDropdown: true,
        showYearDropdown: true,
        maxDate: new Date()
      },
      {
        name: 'purchasePrice',
        type: 'currencyWithSelect',
        label: 'Purchase Price',
        placeholder: 'e.g., 15,000 ',
        required: true,
        colClass: 'col-md-6 mb-4',
        currencyField: 'purchaseCurrency'
      },
      {
        name: 'estimatedCurrentValue',
        type: 'currency',
        label: 'Estimated Current Value',
        placeholder: 'e.g., 22,000 (market value)',
        required: true,
        colClass: 'col-md-6 mb-4'
      }
    ],
    specificFields: [
      {
        name: 'sourceSeller',
        type: 'text',
        label: 'Source / Seller',
        placeholder: "e.g., Christie's Auction, Private Dealer",
        required: false,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'ownershipType',
        type: 'select',
        label: 'Ownership Type',
        required: true,
        colClass: 'col-md-6 mb-4',
        options: [
          { value: 'sole', label: 'Sole' },
          { value: 'joint', label: 'Joint' },
          { value: 'inherited', label: 'Inherited' },
          { value: 'trust', label: 'Trust' },
          { value: 'gifted', label: 'Gifted' },
          { value: 'companyOwned', label: 'Company-owned' }

        ]
      },
      {
        name: 'description',
        type: 'textarea',
        label: 'Description',
        placeholder: 'e.g., Oil on canvas, 24x36 inches, signed lower right, includes certificate of authenticity',
        required: false,
        colClass: 'col-md-12',
        rows: 3
      }
    ],
    api: {
      detailsKey: 'collectiblesDetails',
      detailsFields: [
        'categoryType',
        'description',
        'estimatedCurrentValue',
        'sourceSeller',
        'ownershipType'
      ]
    },

    // Initial values generator for Add mode
    getInitialValues: (defaultCurrency = 'INR') => ({
      name: '',
      categoryType: '',
      purchaseDate: new Date().toISOString().slice(0, 10),
      purchasePrice: '',
      purchaseCurrency: defaultCurrency,
      estimatedCurrentValue: '',
      sourceSeller: '',
      ownershipType: '',
      description: '',
      notes: '',
      isActive: 'yes',
      assetCategory: 'physical',
      assetType: 'collectibles',
      subCategory: 'collectibles'  // Required by base validation
    }),

    // AI extraction field mapper
    extractionMapper: (extractedData) => ({
      name: extractedData.name || '',
      categoryType: extractedData.categoryType || '',
      estimatedCurrentValue: extractedData.estimatedCurrentValue || '',
      sourceSeller: extractedData.sourceSeller || '',
      ownershipType: extractedData.ownershipType || '',
      description: extractedData.description || '',
      purchasePrice: extractedData.purchasePrice || '',
      purchaseCurrency: extractedData.purchaseCurrency || undefined
    }),

    // Payload builder for API submission
    buildPayload: (values) => ({
      detailsKey: 'collectiblesDetails',
      detailsData: {
        name: values.name,
        categoryType: values.categoryType,
        description: values.description,
        estimatedCurrentValue: values.estimatedCurrentValue,
        sourceSeller: values.sourceSeller,
        ownershipType: values.ownershipType
      }
    }),

    // Response mapper for Edit mode (API → Form)
    responseMapper: (apiData) => ({
      categoryType: apiData.collectiblesDetails?.categoryType || '',
      description: apiData.collectiblesDetails?.description || '',
      estimatedCurrentValue: apiData.collectiblesDetails?.estimatedCurrentValue || '',
      sourceSeller: apiData.collectiblesDetails?.sourceSeller || '',
      ownershipType: apiData.collectiblesDetails?.ownershipType || ''
    })
  },

  // Business Ownership configuration
  'business-ownership': {
    // UI customization
    ui: {
      notesPlaceholder: "e.g., Family business, Angel round investment",
      tagsPlaceholder: "e.g., Startup, Long-term"
    },
    commonFields: [
      {
        name: 'name',
        type: 'text',
        label: 'Business Name',
        placeholder: 'e.g., GreenLeaf Foods, ABC Enterprises',
        required: true,
        colClass: 'col-md-12 mb-4'
      },
      {
        name: 'ownershipType',
        type: 'select',
        label: 'Ownership Type',
        required: true,
        colClass: 'col-md-6 mb-4',
        placeholder: 'Select ownership',
        options: [
          { value: 'sole', label: 'Sole' },
          { value: 'joint', label: 'Joint' },
          { value: 'inherited', label: 'Inherited' },
          { value: 'company', label: 'Company' },
          { value: 'companyOwned', label: 'Company Owned' },
          { value: 'shareholder', label: 'Shareholder' },
          { value: 'partner', label: 'Partner' },
          { value: 'soleProprietor', label: 'Sole Proprietor' },
          { value: 'trustBeneficiary', label: 'Trust Beneficiary' },
          { value: 'other', label: 'Other' }
        ]
      },
      {
        name: 'purchaseDate',
        type: 'date',
        label: 'Date of Investment',
        required: true,
        colClass: 'col-md-6 mb-4',
        showMonthDropdown: true,
        showYearDropdown: true,
        maxDate: new Date()
      },
      {
        name: 'purchasePrice',
        type: 'currencyWithSelect',
        label: 'Investment/Purchase Amount',
        placeholder: 'e.g., 50,000',
        required: true,
        colClass: 'col-md-6 mb-4',
        currencyField: 'purchaseCurrency'
      },
      {
        name: 'ownershipPercentage',
        type: 'number',
        label: 'Ownership Percentage (%)',
        placeholder: 'e.g., 25%',
        required: true,
        colClass: 'col-md-6 mb-4'
      }
    ],
    specificFields: [
      {
        name: 'countryOfRegistration',
        type: 'select',
        label: 'Country of Registration',
        required: false,
        colClass: 'col-md-6 mb-4',
        placeholder: 'Select country',
        options: [
          { value: 'India', label: 'India' },
          { value: 'United States', label: 'United States' },
          { value: 'United Kingdom', label: 'United Kingdom' },
          { value: 'New Zealand', label: 'New Zealand' },
          { value: 'Australia', label: 'Australia' },
          { value: 'Other', label: 'Other' }
        ]
      },
      {
        name: 'businessType',
        type: 'select',
        label: 'Business Type',
        required: true,
        colClass: 'col-md-6 mb-4',
        placeholder: 'Select business type',
        options: [
          { value: 'privateCompany', label: 'Private Company' },
          { value: 'startup', label: 'Startup' },
          { value: 'partnership', label: 'Partnership' },
          { value: 'trust', label: 'Trust' },
          { value: 'soleProprietorship', label: 'Sole Proprietorship' },
          { value: 'other', label: 'Other' }

          // { value: 'startup', label: 'Startup' },
        ]
      },
      {
        name: 'equityType',
        type: 'select',
        label: 'Equity Type',
        required: false,
        colClass: 'col-md-6 mb-4',
        placeholder: 'Select equity type',
        options: [
          { value: 'founder', label: 'Founder' },
          { value: 'coFounder', label: 'Co-Founder' },
          { value: 'angelInvestor', label: 'Angel Investor' },
          { value: 'seedInvestor', label: 'Seed Investor' },
          { value: 'vcPeInvestor', label: 'VC / PE Investor' },
          { value: 'esop', label: 'ESOP' },
          { value: 'partner', label: 'Partner' },
          { value: 'other', label: 'Other' }
        ]
      },
      {
        name: 'valuationSource',
        type: 'text',
        label: 'Valuation Source',
        placeholder: 'e.g., Last funding round',
        required: false,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'estimatedCurrentValue',
        type: 'currency',
        label: 'Estimated Current Value',
        placeholder: 'e.g., 250,000',
        required: true,
        colClass: 'col-md-6 mb-4'
      }
    ],
    api: {
      detailsKey: 'businessOwnershipDetails',
      detailsFields: [
        'ownershipType',
        'ownershipPercentage',
        'countryOfRegistration',
        'businessType',
        'equityType',
        'valuationSource',
        'estimatedCurrentValue'
      ]
    },

    // Initial values generator for Add mode
    getInitialValues: (defaultCurrency = 'INR') => ({
      name: '',
      ownershipType: '',
      purchaseDate: new Date().toISOString().slice(0, 10),
      purchasePrice: '',
      purchaseCurrency: defaultCurrency,
      ownershipPercentage: '',
      countryOfRegistration: '',
      businessType: '',
      valuationSource: '',
      estimatedCurrentValue: '',
      notes: '',
      isActive: 'yes',
      assetCategory: 'digital',
      assetType: 'business-ownership',
      subCategory: 'business-ownership'  // Required by base validation
    }),

    // AI extraction field mapper
    extractionMapper: (extractedData) => ({
      name: extractedData.businessName || extractedData.name || '',
      ownershipType: extractedData.ownershipType || '',
      ownershipPercentage: extractedData.ownershipPercentage || '',
      countryOfRegistration: extractedData.countryOfRegistration || '',
      businessType: extractedData.businessType || '',
      valuationSource: extractedData.valuationSource || '',
      estimatedCurrentValue: extractedData.estimatedCurrentValue || '',
      purchasePrice: extractedData.purchasePrice || '',
      purchaseCurrency: extractedData.purchaseCurrency || undefined
    }),

    // Payload builder for API submission
    buildPayload: (values) => ({
      detailsKey: 'businessOwnershipDetails',
      detailsData: {
        businessName: values.name,
        ownershipType: values.ownershipType,
        ownershipPercentage: values.ownershipPercentage,
        countryOfRegistration: values.countryOfRegistration,
        businessType: values.businessType,
        equityType: values.equityType,
        valuationSource: values.valuationSource,
        estimatedCurrentValue: values.estimatedCurrentValue
      }
    }),

    // Response mapper for Edit mode (API → Form)
    responseMapper: (apiData) => ({
      ownershipType: apiData.businessOwnershipDetails?.ownershipType || '',
      ownershipPercentage: apiData.businessOwnershipDetails?.ownershipPercentage || '',
      countryOfRegistration: apiData.businessOwnershipDetails?.countryOfRegistration || '',
      businessType: apiData.businessOwnershipDetails?.businessType || '',
      equityType: apiData.businessOwnershipDetails?.equityType || '',
      valuationSource: apiData.businessOwnershipDetails?.valuationSource || '',
      estimatedCurrentValue: apiData.businessOwnershipDetails?.estimatedCurrentValue || ''
    })
  },

  // Other Physical Assets configuration
  'other-physical-assets': {
    ui: {
      notesPlaceholder: "e.g., Purchased from JB Hi-Fi, includes original box and accessories",
      tagsPlaceholder: "e.g., Electronics, Business"
    },
    commonFields: [
      {
        name: 'name',
        type: 'text',
        label: 'Asset Name',
        placeholder: 'e.g., Canon Camera, MacBook Pro',
        required: true,
        colClass: 'col-md-12 mb-4'
      },
      {
        name: 'category',
        type: 'select',
        label: 'Category',
        required: true,
        colClass: 'col-md-6 mb-4',
        options: [
          { value: 'electronics', label: 'Electronics' },
          { value: 'furniture', label: 'Furniture' },
          { value: 'equipment', label: 'Equipment' },
          { value: 'appliance', label: 'Appliance' },
          { value: 'other', label: 'Other' }
        ]
      },
      {
        name: 'purchaseDate',
        type: 'date',
        label: 'Purchase Date',
        required: true,
        colClass: 'col-md-6 mb-4',
        showMonthDropdown: true,
        showYearDropdown: true,
        maxDate: new Date()
      },
      {
        name: 'purchasePrice',
        type: 'currencyWithSelect',
        label: 'Purchase Price',
        placeholder: 'e.g., 2,500',
        required: true,
        colClass: 'col-md-6 mb-4',
        currencyField: 'purchaseCurrency'
      },
      {
        name: 'brandModel',
        type: 'text',
        label: 'Brand / Model',
        placeholder: 'e.g., Apple MacBook Pro, Canon EOS R5',
        required: false,
        colClass: 'col-md-6 mb-4'
      }
    ],
    specificFields: [
      {
        name: 'estimatedCurrentValue',
        type: 'currency',
        label: 'Estimated Current Value',
        placeholder: 'e.g., 2,200 (market value)',
        required: true,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'serialNumber',
        type: 'text',
        label: 'Serial Number / ID',
        placeholder: 'e.g., SN-ABC12345',
        required: false,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'ownershipType',
        type: 'select',
        label: 'Ownership Type',
        required: true,
        colClass: 'col-md-6 mb-4',
        options: [
          { value: 'sole', label: 'Sole' },
          { value: 'joint', label: 'Joint' },
          { value: 'inherited', label: 'Inherited' },
          { value: 'trust', label: 'Trust' },
          { value: 'gifted', label: 'Gifted' },
          { value: 'companyOwned', label: 'Company-owned' }
        ]
      }
    ],
    api: {
      detailsKey: 'otherPhysicalAssetsDetails',
      detailsFields: [
        'category',
        'brandModel',
        'estimatedCurrentValue',
        'serialNumber',
        'ownershipType'
      ]
    },

    // Initial values generator for Add mode
    getInitialValues: (defaultCurrency = 'INR') => ({
      name: '',
      category: '',
      purchaseDate: new Date().toISOString().slice(0, 10),
      purchasePrice: '',
      purchaseCurrency: defaultCurrency,
      brandModel: '',
      estimatedCurrentValue: '',
      serialNumber: '',
      ownershipType: '',
      notes: '',
      isActive: 'yes',
      assetCategory: 'physical',
      assetType: 'other-physical-assets',
      subCategory: 'other-physical-assets'  // Required by base validation
    }),

    // AI extraction field mapper
    extractionMapper: (extractedData) => {
      // Handle nested otherPhysicalAssetsDetails structure
      const details = extractedData.otherPhysicalAssetsDetails || {};

      // Helper function to safely convert to lowercase
      const toLower = (value) => {
        if (typeof value === 'string') return value.toLowerCase();
        if (value && typeof value === 'object') return '';
        return String(value || '').toLowerCase();
      };

      // Combine brand and model if they're separate fields
      let brandModel = details.brandModel || extractedData.brandModel || '';
      if (!brandModel) {
        const brand = details.brand || extractedData.brand || '';
        const model = details.model || extractedData.model || '';
        brandModel = [brand, model].filter(Boolean).join(' ');
      }

      return {
        name: extractedData.name || extractedData.assetName || '',
        category: toLower(details.category || extractedData.category),
        brandModel: brandModel,
        estimatedCurrentValue: extractedData.estimatedValue || details.estimatedCurrentValue || extractedData.estimatedCurrentValue || '',
        serialNumber: details.serialNumber || extractedData.serialNumber || '',
        ownershipType: toLower(details.ownershipType || extractedData.ownershipType),
        purchasePrice: extractedData.purchasePrice || '',
        purchaseDate: extractedData.purchaseDate || '',
        purchaseCurrency: extractedData.purchaseCurrency || undefined
      };
    },

    // Payload builder for API submission
    buildPayload: (values) => ({
      detailsKey: 'otherPhysicalAssetsDetails',
      detailsData: {
        assetName: values.name,
        category: values.category,
        brandModel: values.brandModel,
        estimatedCurrentValue: values.estimatedCurrentValue,
        serialNumber: values.serialNumber,
        ownershipType: values.ownershipType
      }
    }),

    // Response mapper for Edit mode (API → Form)
    responseMapper: (apiData) => ({
      category: apiData.otherPhysicalAssetsDetails?.category || '',
      brandModel: apiData.otherPhysicalAssetsDetails?.brandModel || '',
      estimatedCurrentValue: apiData.otherPhysicalAssetsDetails?.estimatedCurrentValue || '',
      serialNumber: apiData.otherPhysicalAssetsDetails?.serialNumber || '',
      ownershipType: apiData.otherPhysicalAssetsDetails?.ownershipType || ''
    })
  },

  // Important Papers configuration
  'important-papers': {
    // UI customization
    ui: {
      notesPlaceholder: "e.g., Stored in bank locker, Original copy",
      tagsPlaceholder: "e.g., Identity, Legal"
    },
    commonFields: [
      {
        name: 'name',
        type: 'text',
        label: 'Document Name',
        placeholder: 'e.g., Passport, Birth Certificate',
        required: true,
        colClass: 'col-md-12 mb-4'
      },
      {
        name: 'category',
        type: 'select',
        label: 'Document Category',
        placeholder: 'Select category',
        required: true,
        colClass: 'col-md-6 mb-4',
        options: [
          { value: 'identity', label: 'Identity' },
          { value: 'legal', label: 'Legal' },
          { value: 'financial', label: 'Financial' },
          { value: 'property', label: 'Property' },
          { value: 'insurance', label: 'Insurance' },
          { value: 'medical', label: 'Medical' },
          { value: 'education', label: 'Education' },
          { value: 'immigration', label: 'Immigration' },
          { value: 'business', label: 'Business' },
          { value: 'other', label: 'Other' }
        ]
      },
      // {
      //   name: 'purchaseDate',
      //   type: 'date',
      //   label: 'Date Added',
      //   required: true,
      //   colClass: 'col-md-6 mb-4',
      //   showMonthDropdown: true,
      //   showYearDropdown: true,
      //   maxDate: new Date()
      // },
      {
        name: 'ownershipType',
        type: 'select',
        label: 'Ownership Type',
        placeholder: 'Select ownership',
        required: true,
        colClass: 'col-md-6 mb-4',
        options: [
          { value: 'sole', label: 'Sole' },
          { value: 'joint', label: 'Joint' },
          { value: 'family', label: 'Family' },
          { value: 'business', label: 'Business' },
          { value: 'trust', label: 'Trust' },
          { value: 'other', label: 'Other' },
          // { value: 'company', label: 'Company' }
        ]
      },
      {
        name: 'issuingAuthority',
        type: 'text',
        label: 'Issuing Authority',
        placeholder: 'e.g., New Zealand Government',
        required: false,
        colClass: 'col-md-6 mb-4'
      }
    ],
    specificFields: [
      {
        name: 'documentNumber',
        type: 'text',
        label: 'Document Number',
        placeholder: 'e.g., DL456789, NZ-1234567',
        required: false,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'issueDate',
        type: 'date',
        label: 'Issue Date',
        placeholder: 'Select issue date',
        required: false,
        colClass: 'col-md-6 mb-4',
        showMonthDropdown: true,
        showYearDropdown: true,
        maxDate: new Date()
      },
      {
        name: 'expiryDate',
        type: 'date',
        label: 'Expiry Date',
        placeholder: 'Select expiry date',
        required: false,
        colClass: 'col-md-6 mb-4',
        showMonthDropdown: true,
        showYearDropdown: true,
        minDate: 'issueDate'
      },
      {
        name: 'linkedAsset',
        type: 'select',
        label: 'Linked Asset',
        required: false,
        colClass: 'col-md-6 mb-4',
        placeholder: 'Select linked asset',
        options: [
          { value: 'familyHome', label: 'Family Home' },
          { value: 'car', label: 'Car' },
          { value: 'investmentProperty', label: 'Investment Property' },
          { value: 'insurancePolicy', label: 'Insurance Policy' },
          { value: 'other', label: 'Other' }
        ]
      },
      {
        name: 'documentStatus',
        type: 'select',
        label: 'Document Status',
        placeholder: 'Select status',
        required: false,
        colClass: 'col-md-6 mb-4',
        options: [
          { value: 'active', label: 'Active' },
          { value: 'expired', label: 'Expired' },
          { value: 'replaced', label: 'Replaced' },
          { value: 'lost', label: 'Lost' },
          { value: 'archived', label: 'Archived' }
        ]
      },
      {
        name: 'documentType',
        type: 'select',
        label: 'Document Type',
        placeholder: 'Select type',
        required: false,
        colClass: 'col-md-6 mb-4',
        options: [
          { value: 'governmentIssued', label: 'Government Issued' },
          { value: 'legalContract', label: 'Legal Contract' },
          { value: 'financialRecord', label: 'Financial Record' },
          { value: 'certificate', label: 'Certificate' },
          { value: 'licence', label: 'Licence' },
          { value: 'policyDocument', label: 'Policy Document' },
          { value: 'other', label: 'Other' }
        ]
      }
    ],
    api: {
      detailsKey: 'importantPapersDetails',
      detailsFields: [
        'category',
        'issuingAuthority',
        'documentNumber',
        'issueDate',
        'expiryDate',
        'linkedAsset',
        'documentStatus',
        'documentType',
        'ownershipType'
      ]
    },

    // Initial values generator for Add mode
    getInitialValues: () => ({
      name: '',
      category: '',
      purchaseDate: new Date().toISOString().slice(0, 10),
      ownershipType: '',
      issuingAuthority: '',
      documentNumber: '',
      issueDate: '',
      expiryDate: '',
      linkedAsset: '',
      documentStatus: '',
      documentType: '',
      notes: '',
      isActive: 'yes',
      assetCategory: 'physical',
      assetType: 'important-papers',
      subCategory: 'important-papers'  // Required by base validation
    }),

    // AI extraction field mapper
    extractionMapper: (extractedData) => ({
      name: extractedData.documentName || extractedData.name || '',
      category: extractedData.category || '',
      ownershipType: extractedData.ownershipType || '',
      issuingAuthority: extractedData.issuingAuthority || '',
      documentNumber: extractedData.documentNumber || '',
      issueDate: extractedData.issueDate || '',
      expiryDate: extractedData.expiryDate || '',
      linkedAsset: extractedData.linkedAsset || '',
      documentStatus: extractedData.documentStatus || '',
      documentType: extractedData.documentType || ''
    }),

    // Payload builder for API submission
    buildPayload: (values) => ({
      detailsKey: 'importantPapersDetails',
      detailsData: {
        documentName: values.name,
        category: values.category,
        issuingAuthority: values.issuingAuthority,
        documentNumber: values.documentNumber,
        issueDate: values.issueDate,
        expiryDate: values.expiryDate,
        linkedAsset: values.linkedAsset,
        documentStatus: values.documentStatus,
        documentType: values.documentType,
        ownershipType: values.ownershipType
      }
    }),

    // Response mapper for Edit mode (API → Form)
    responseMapper: (apiData) => ({
      category: apiData.importantPapersDetails?.category || '',
      issuingAuthority: apiData.importantPapersDetails?.issuingAuthority || '',
      documentNumber: apiData.importantPapersDetails?.documentNumber || '',
      issueDate: apiData.importantPapersDetails?.issueDate ? apiData.importantPapersDetails.issueDate.slice(0, 10) : '',
      expiryDate: apiData.importantPapersDetails?.expiryDate ? apiData.importantPapersDetails.expiryDate.slice(0, 10) : '',
      linkedAsset: apiData.importantPapersDetails?.linkedAsset || '',
      documentStatus: apiData.importantPapersDetails?.documentStatus || '',
      documentType: apiData.importantPapersDetails?.documentType || '',
      ownershipType: apiData.importantPapersDetails?.ownershipType || ''
    })
  },

  // Precious Metals configuration
  'precious-metals': {
    ui: {
      notesPlaceholder: "e.g., Perth Mint certificate #12345",
      tagsPlaceholder: "e.g., Investment, Long-term"
    },
    commonFields: [
      {
        name: 'name',
        type: 'text',
        label: 'Asset Name',
        placeholder: 'e.g., Gold Coin Collection, Wedding Ring',
        required: true,
        colClass: 'col-md-12 mb-4'
      },
      {
        name: 'metalType',
        type: 'select',
        label: 'Metal Type',
        required: true,
        colClass: 'col-md-6 mb-4',
        options: [
          { value: 'gold', label: 'Gold' },
          { value: 'silver', label: 'Silver' },
          { value: 'platinum', label: 'Platinum' },
          { value: 'Other', label: 'Other'}
        ]
      },
      {
        name: 'purchaseDate',
        type: 'date',
        label: 'Purchase Date',
        required: true,
        colClass: 'col-md-6 mb-4',
        showMonthDropdown: true,
        showYearDropdown: true,
        maxDate: new Date()
      },
      {
        name: 'purchasePrice',
        type: 'currencyWithSelect',
        label: 'Purchase Price',
        placeholder: 'e.g., 8,500',
        required: true,
        colClass: 'col-md-6 mb-4',
        currencyField: 'purchaseCurrency'
      },
      {
        name: 'formType',
        type: 'select',
        label: 'Form / Sub-Type',
        required: true,
        colClass: 'col-md-6 mb-4',
        options: [
          {value: 'bar', label: 'Bar' },
          { value: 'coin', label: 'Coin' },
          { value: 'bullion', label: 'Bullion' },
          { value: 'jewellery', label: 'Jewellery' },
          { value: 'other', label: 'Other' }
        ]
      }
    ],
    specificFields: [
      {
        name: 'weight',
        type: 'number',
        label: 'Weight / Quantity',
        placeholder: 'e.g., 50 or 2.5',
        required: false,
        colClass: 'col-md-3 mb-4'
      },
      {
        name: 'weightUnit',
        type: 'select',
        label: 'Unit',
        required: false,
        colClass: 'col-md-3 mb-4',
        options: [
          { value: 'ounce', label: 'Ounce' },
          { value: 'gram', label: 'Gram' },
          { value: 'kilogram', label: 'Kilogram' },
          { value: 'piece', label: 'Piece' }
        ]
      },
      {
        name: 'purity',
        type: 'percentage',
        label: 'Purity',
        placeholder: 'e.g., 99.9% or 24K',
        required: false,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'currentMarketPrice',
        type: 'currency',
        label: 'Current Market Price (per unit)',
        placeholder: 'e.g., 110',
        required: false,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'estimatedCurrentValue',
        type: 'currency',
        label: 'Estimated Current Value',
        placeholder: 'e.g., 12,000 (market value)',
        required: true,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'storageLocation',
        type: 'text',
        label: 'Storage Location',
        placeholder: 'e.g., Vault Storage Ltd',
        required: false,
        colClass: 'col-md-6 mb-4'
      },
      {
        name: 'ownershipType',
        type: 'select',
        label: 'Ownership Type',
        required: false,
        colClass: 'col-md-6 mb-4',
        options: [
          { value: 'sole', label: 'Sole' },
          { value: 'joint', label: 'Joint' },
          { value: 'inherited', label: 'Inherited' },
          { value: 'trust', label: 'Trust' },
          { value: 'company', label: 'Company'}
        ]
      }
    ],
    api: {
      detailsKey: 'preciousMetalsDetails',
      detailsFields: [
        'metalType',
        'formType',
        'weight',
        'weightUnit',
        'purity',
        'currentMarketPrice',
        'estimatedCurrentValue',
        'storageLocation',
        'ownershipType'
      ]
    },

    // Initial values generator for Add mode
    getInitialValues: (defaultCurrency = 'INR') => ({
      name: '',
      metalType: '',
      purchaseDate: new Date().toISOString().slice(0, 10),
      purchasePrice: '',
      purchaseCurrency: defaultCurrency,
      formType: '',
      weight: '',
      weightUnit: 'gram',
      purity: '',
      currentMarketPrice: '',
      estimatedCurrentValue: '',
      storageLocation: '',
      ownershipType: '',
      notes: '',
      isActive: 'yes',
      assetCategory: 'physical',
      assetType: 'precious-metals',
      subCategory: 'precious-metals'  // Required by base validation
    }),

    // AI extraction field mapper
    extractionMapper: (extractedData) => {
      // Handle nested preciousMetalDetails structure
      const details = extractedData.preciousMetalDetails || {};

      // Helper function to safely convert to lowercase
      const toLower = (value) => {
        if (typeof value === 'string') return value.toLowerCase();
        if (value && typeof value === 'object') return '';
        return String(value || '').toLowerCase();
      };

      return {
        name: details.assetName || extractedData.assetName || extractedData.name || '',
        metalType: toLower(details.metalType || extractedData.metalType),
        formType: toLower(details.form || extractedData.form || details.formType || extractedData.formType),
        weight: details.weight || extractedData.weight || extractedData.weightQuantity || '',
        weightUnit: toLower(details.weightUnit || extractedData.weightUnit || 'gram'),
        purity: details.purity || extractedData.purity || '',
        currentMarketPrice: details.currentMarketPricePerGram || extractedData.currentMarketPricePerGram || details.currentMarketPrice || extractedData.currentMarketPrice || '',
        estimatedCurrentValue: extractedData.estimatedValue || details.estimatedCurrentValue || extractedData.estimatedCurrentValue || '',
        storageLocation: details.storageLocation || extractedData.storageLocation || '',
        ownershipType: toLower(details.ownershipType || extractedData.ownershipType),
        purchasePrice: extractedData.purchasePrice || '',
        purchaseDate: extractedData.purchaseDate || '',
        purchaseCurrency: extractedData.purchaseCurrency || undefined
      };
    },

    // Payload builder for API submission
    buildPayload: (values) => {
      const detailsData = {
        assetName: values.name,
        metalType: values.metalType,
        formType: values.formType,
        weight: values.weight,
        weightUnit: values.weightUnit,
        purity: values.purity,
        currentMarketPrice: values.currentMarketPrice,
        estimatedCurrentValue: values.estimatedCurrentValue,
        storageLocation: values.storageLocation
      };
      // Only include ownershipType if it has a non-empty value
      if (values.ownershipType && values.ownershipType.trim() !== "") {
        detailsData.ownershipType = values.ownershipType;
      }
      return {
        detailsKey: 'preciousMetalsDetails',
        detailsData
      };
    },

    // Response mapper for Edit mode (API → Form)
    responseMapper: (apiData) => ({
      metalType: apiData.preciousMetalsDetails?.metalType || '',
      formType: apiData.preciousMetalsDetails?.formType || '',
      weight: apiData.preciousMetalsDetails?.weight || '',
      weightUnit: apiData.preciousMetalsDetails?.weightUnit || 'gram',
      purity: apiData.preciousMetalsDetails?.purity || '',
      currentMarketPrice: apiData.preciousMetalsDetails?.currentMarketPrice || '',
      estimatedCurrentValue: apiData.preciousMetalsDetails?.estimatedCurrentValue || '',
      storageLocation: apiData.preciousMetalsDetails?.storageLocation || '',
      ownershipType: apiData.preciousMetalsDetails?.ownershipType || undefined
    })
  }
  // More asset types will be added here
}

// Helper to get field config for a specific asset type
export const getFieldConfig = (subCategory) => {
  // Support both formats: "bank accounts" and "bank-accounts"
  const normalizedSubCategory = subCategory;
  let config = FIELD_CONFIGS[normalizedSubCategory];

  // If not found and contains space, try with hyphen
  if (!config && normalizedSubCategory.includes(' ')) {
    const kebabCase = normalizedSubCategory.replace(/\s+/g, '-');
    config = FIELD_CONFIGS[kebabCase];
  }

  // If not found and contains hyphen, try with space
  if (!config && normalizedSubCategory.includes('-')) {
    const spaceCase = normalizedSubCategory.replace(/-/g, ' ');
    config = FIELD_CONFIGS[spaceCase];
  }

  return config || {
    commonFields: [],
    specificFields: [],
    sidebarFields: [],
    api: {}
  };
}
